import * as React from "react";
import {TextField} from "web-com";
import {ReactNode, useEffect, useState} from "react";
import {getRequestExternal} from "utils/externalApiCalls";
import {Card} from "web-com";
import {FIELD_NAMES} from "profile/enums";
import {COLOR, INPUT_TYPE, StyleType} from "utils/variables";
import {IBankAccountProfile} from "IncompleteInfoV2/LoanRequest/utils";
import {ifscVerifyLink} from "config";

interface IProps {
    profile: IBankAccountProfile;
    onChange: (fieldName: string, value: string) => void;
    errors: {[key: string]: string};
}

interface IBankDetails {
    bank: string;
    city: string;
    state: string;
    branch: string;
}

export const renderBankDetails = (bankDetails: IBankDetails): ReactNode => {
    return Object.keys(bankDetails).map((key) => {
        return (
            <div className="bank-details" key={key}>
                <div>
                    {`${key.toUpperCase()}:`}
                </div>
                <div>
                    {bankDetails[key]}
                </div>
            </div>
        );
    });
};

const IFSC_CODE_LENGTH = 11;
export const BankAccount = (props: IProps) => {
    const [bankDetails, updateBankDetails] = useState<IBankDetails>(null);
    const [ifscCodeErrorMessage, updateIfscCodeErrorMessage] = useState<string>(null);

    useEffect(() => {
        const value = props.profile[FIELD_NAMES.IFSC];
        if (value) {
            updateAndCheckIfscCode(value);
        }
    }, []);

    const updateAndCheckIfscCode = async (ifsc: string) => {
        if (ifsc.length === IFSC_CODE_LENGTH) {
            try {
                const ifscResponse: any = await getRequestExternal(`${ifscVerifyLink}/${ifsc}`, null);
                if (ifscResponse) {
                    const ifscDetails = JSON.parse(ifscResponse);
                    updateBankDetails({
                        bank: ifscDetails.BANK,
                        city: ifscDetails.CITY,
                        state: ifscDetails.STATE,
                        branch: ifscDetails.BRANCH,
                    });
                }
                updateIfscCodeErrorMessage(null);
            } catch (err) {
                updateIfscCodeErrorMessage(err.status === 404 ? "Incorrect IFSC code!" : "Something went wrong!");
                updateBankDetails(null);
            }
        } else if (ifsc.length > 0) {
            updateIfscCodeErrorMessage("Incorrect IFSC code!");
            updateBankDetails(null);
        } else {
            updateIfscCodeErrorMessage("");
        }
    };

    const handleChange = (fieldName: string, value: string) => {
        if (fieldName === FIELD_NAMES.IFSC && value) {
            updateAndCheckIfscCode(value);
            value = value.toUpperCase();
        }
        props.onChange(fieldName, value);
    };

    return (
        <Card headerTitle="Bank Account Details"
            headerBackgroundColor={COLOR.GRAY_BG}
            headerTextColor={COLOR.BLACK}
        >
            <TextField
                type={INPUT_TYPE.TEXT}
                label="Account Holder Name"
                placeholder="Please enter beneficiary name"
                value={props.profile[FIELD_NAMES.BENEFICIARY_NAME] || null}
                fieldName={FIELD_NAMES.BENEFICIARY_NAME}
                onChange={handleChange}
                styleType={StyleType.BORDER_BOTTOM_INPUT}
                errorMsg={props.errors[FIELD_NAMES.BENEFICIARY_NAME] || null}
            />
            <TextField
                type={INPUT_TYPE.NUMBER}
                label="Account Number"
                placeholder="Please enter account number"
                value={props.profile[FIELD_NAMES.ACCOUNT_NUMBER] || null}
                fieldName={FIELD_NAMES.ACCOUNT_NUMBER}
                onChange={handleChange}
                styleType={StyleType.BORDER_BOTTOM_INPUT}
                errorMsg={props.errors[FIELD_NAMES.ACCOUNT_NUMBER] || null}
            />
            <TextField
                type={INPUT_TYPE.TEXT}
                label="IFSC Code"
                placeholder={"Please enter IFSC code"}
                value={props.profile[FIELD_NAMES.IFSC] || null}
                fieldName={FIELD_NAMES.IFSC}
                onChange={handleChange}
                styleType={StyleType.BORDER_BOTTOM_INPUT}
                errorMsg={ifscCodeErrorMessage || props.errors[FIELD_NAMES.IFSC] || null}
            />
            <div className="flex-column-center-row">
                {bankDetails ? <div className="bank-details-container">
                    {renderBankDetails(bankDetails)}
                </div> : null}
            </div>
        </Card>
    );
};

export default BankAccount;
